import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Renders a single CMS block. Add new types here as the editor grows.
 * Every block receives `{type, data, id}`; missing fields render gracefully.
 */
export default function BlockRenderer({ block }) {
  if (!block) return null;
  switch (block.type) {
    case 'hero': return <HeroBlock {...block.data} />;
    case 'rich_text': return <RichTextBlock {...block.data} />;
    case 'service_grid': return <ServiceGridBlock {...block.data} />;
    case 'quote': return <QuoteBlock {...block.data} />;
    case 'video': return <VideoBlock {...block.data} />;
    case 'two_column': return <TwoColumnBlock {...block.data} />;
    case 'cta_banner': return <CTABannerBlock {...block.data} />;
    case 'contact_form': return <ContactFormBlock {...block.data} />;
    case 'spacer': return <SpacerBlock {...block.data} />;
    default:
      return (
        <div className="max-w-4xl mx-auto my-6 p-4 border border-dashed border-amber-400 bg-amber-50 text-amber-800 text-sm rounded">
          Unknown block type: <code>{block.type}</code>
        </div>
      );
  }
}

function CTALink({ href, children, primary }) {
  if (!href) return null;
  const cls = primary
    ? 'inline-flex items-center px-5 py-2.5 rounded-md font-semibold text-white bg-[var(--site-primary,#1a3a5c)] hover:opacity-90 transition'
    : 'inline-flex items-center px-5 py-2.5 rounded-md font-medium border hover:bg-muted transition';
  if (href.startsWith('http')) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>{children}</a>;
  }
  return <Link to={href} className={cls}>{children}</Link>;
}

/* ----- block implementations ----- */
function HeroBlock({ eyebrow, heading, subheading, image_url, primary_cta_label, primary_cta_url, secondary_cta_label, secondary_cta_url }) {
  return (
    <section className="bg-gradient-to-b from-white to-[#f4f6f9] border-b">
      <div className="max-w-6xl mx-auto px-4 py-16 sm:py-20 grid md:grid-cols-2 gap-10 items-center">
        <div>
          {eyebrow && (
            <p className="uppercase tracking-widest text-xs font-semibold text-[var(--site-primary,#1a3a5c)] mb-3">
              {eyebrow}
            </p>
          )}
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground">
            {heading}
          </h1>
          {subheading && (
            <p className="mt-5 text-lg text-muted-foreground max-w-prose leading-relaxed">{subheading}</p>
          )}
          {(primary_cta_label || secondary_cta_label) && (
            <div className="mt-7 flex flex-wrap gap-3">
              {primary_cta_label && <CTALink href={primary_cta_url} primary>{primary_cta_label}</CTALink>}
              {secondary_cta_label && <CTALink href={secondary_cta_url}>{secondary_cta_label}</CTALink>}
            </div>
          )}
        </div>
        {image_url && (
          <div className="relative">
            <img src={image_url} alt="" className="rounded-2xl shadow-xl object-cover w-full h-80 sm:h-96" loading="lazy" />
          </div>
        )}
      </div>
    </section>
  );
}

function RichTextBlock({ heading, body_markdown }) {
  return (
    <section className="max-w-3xl mx-auto px-4 py-10">
      {heading && <h2 className="font-heading text-2xl sm:text-3xl font-semibold mb-4">{heading}</h2>}
      {body_markdown && (
        <div className="prose prose-neutral max-w-none whitespace-pre-line text-foreground/90 leading-relaxed">
          {body_markdown}
        </div>
      )}
    </section>
  );
}

function ServiceGridBlock({ heading, subheading, items = [] }) {
  return (
    <section className="max-w-6xl mx-auto px-4 py-14">
      {(heading || subheading) && (
        <div className="text-center mb-10">
          {heading && <h2 className="font-heading text-3xl sm:text-4xl font-semibold">{heading}</h2>}
          {subheading && <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">{subheading}</p>}
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((it, i) => {
          const inner = (
            <div className="group h-full rounded-xl overflow-hidden border bg-white hover:shadow-lg transition">
              {it.image_url ? (
                <div
                  className="h-40 bg-cover bg-center"
                  style={{ backgroundImage: `url(${it.image_url})` }}
                />
              ) : (
                <div className="h-40 bg-gradient-to-br from-[var(--site-primary,#1a3a5c)]/15 via-[var(--site-primary,#1a3a5c)]/5 to-transparent" />
              )}
              <div className="p-5">
                <h3 className="font-heading text-xl font-semibold text-[var(--site-primary,#1a3a5c)] group-hover:underline">
                  {it.title}
                </h3>
                {it.description && <p className="mt-2 text-sm text-muted-foreground">{it.description}</p>}
              </div>
            </div>
          );
          return it.link_url
            ? <Link key={i} to={it.link_url}>{inner}</Link>
            : <div key={i}>{inner}</div>;
        })}
      </div>
    </section>
  );
}

function QuoteBlock({ quote, attribution }) {
  return (
    <section className="max-w-3xl mx-auto px-6 py-10">
      <blockquote className="border-l-4 border-[var(--site-primary,#1a3a5c)] pl-6 italic text-xl sm:text-2xl text-foreground/90 leading-relaxed">
        “{quote}”
      </blockquote>
      {attribution && (
        <p className="mt-3 text-sm uppercase tracking-wider text-muted-foreground">— {attribution}</p>
      )}
    </section>
  );
}

function VideoBlock({ heading, url, caption }) {
  const embed = resolveVideoEmbed(url);
  return (
    <section className="max-w-5xl mx-auto px-4 py-10">
      {heading && <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-center mb-5">{heading}</h2>}
      <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black shadow-lg">
        {embed ? (
          <iframe
            src={embed}
            title={heading || 'video'}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="autoplay; encrypted-media; fullscreen"
            allowFullScreen
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center text-white/70 text-sm border-2 border-dashed border-white/30 m-4 rounded-lg">
            Paste a Google Drive or YouTube video URL in the editor to display it here.
          </div>
        )}
      </div>
      {caption && <p className="mt-3 text-center text-sm text-muted-foreground">{caption}</p>}
    </section>
  );
}

function TwoColumnBlock({ heading, body_markdown, image_url, image_side = 'right' }) {
  const imgFirst = image_side === 'left';
  return (
    <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
      <div className={imgFirst ? 'order-2 md:order-1' : ''}>
        {heading && <h2 className="font-heading text-3xl font-semibold mb-4">{heading}</h2>}
        {body_markdown && (
          <div className="prose prose-neutral max-w-none whitespace-pre-line text-foreground/90 leading-relaxed">
            {body_markdown}
          </div>
        )}
      </div>
      <div className={imgFirst ? 'order-1 md:order-2' : ''}>
        {image_url ? (
          <img src={image_url} alt="" className="rounded-xl w-full object-cover h-80 shadow" loading="lazy" />
        ) : (
          <div className="rounded-xl h-80 bg-muted grid place-items-center text-muted-foreground text-sm">
            Image placeholder
          </div>
        )}
      </div>
    </section>
  );
}

function CTABannerBlock({ heading, subheading, cta_label, cta_url, background_color }) {
  const bg = background_color || 'var(--site-primary, #1a3a5c)';
  return (
    <section className="py-12">
      <div className="max-w-5xl mx-auto px-6 py-10 rounded-2xl text-white text-center" style={{ backgroundColor: bg }}>
        {heading && <h2 className="font-heading text-2xl sm:text-3xl font-semibold">{heading}</h2>}
        {subheading && <p className="mt-3 text-white/85">{subheading}</p>}
        {cta_label && (
          <div className="mt-6">
            <CTALink href={cta_url || '#'}>
              <span className="text-[var(--site-primary,#1a3a5c)]">{cta_label}</span>
            </CTALink>
          </div>
        )}
      </div>
    </section>
  );
}

function ContactFormBlock({ heading = 'Contact Us', subheading, submit_label = 'Send' }) {
  const [form, setForm] = React.useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState('');

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const res = await fetch(`${API}/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSent(true);
    } catch (err) {
      setError("Couldn't submit. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="max-w-2xl mx-auto px-4 py-12">
      <div className="text-center mb-6">
        {heading && <h2 className="font-heading text-3xl font-semibold">{heading}</h2>}
        {subheading && <p className="mt-2 text-muted-foreground">{subheading}</p>}
      </div>
      {sent ? (
        <div className="rounded-lg bg-green-50 border border-green-200 p-6 text-center text-green-800">
          Thanks for reaching out — we'll be in touch shortly.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="cms-contact-form">
          <input required placeholder="Name *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border rounded-md" data-testid="contact-name" />
          <input required type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border rounded-md" data-testid="contact-email" />
          <input placeholder="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="w-full px-3 py-2 border rounded-md" data-testid="contact-phone" />
          <textarea required placeholder="Message *" rows={5} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} className="w-full px-3 py-2 border rounded-md" data-testid="contact-message" />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button type="submit" disabled={sending} className="w-full px-5 py-2.5 rounded-md text-white bg-[var(--site-primary,#1a3a5c)] hover:opacity-90 disabled:opacity-60 font-semibold" data-testid="contact-submit">
            {sending ? 'Sending…' : submit_label}
          </button>
        </form>
      )}
    </section>
  );
}

function SpacerBlock({ size = 'md' }) {
  const sizes = { sm: 'h-8', md: 'h-16', lg: 'h-32' };
  return <div className={sizes[size] || sizes.md} aria-hidden />;
}

/* ----- helpers ----- */
export function resolveVideoEmbed(url) {
  if (!url) return null;
  const u = String(url).trim();
  // Google Drive
  let m = u.match(/drive\.google\.com\/(?:file\/d\/|open\?id=|uc\?id=)([\w-]+)/);
  if (m) return `https://drive.google.com/file/d/${m[1]}/preview`;
  // YouTube
  m = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  // Vimeo
  m = u.match(/vimeo\.com\/(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  if (u.startsWith('http')) return u;
  return null;
}
